import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import LabelWrapper from "components/Common/LabelWrapper";
import CountUp from "react-countup";
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import {
  COLOR_RESERVATION_USER_TYPE,
  PRE_REGISTER_SOURCE_OPTION,
  RESERVATION_EXPERIENCE_LEVEL,
  RESERVATION_USER_TYPE,
} from "helpers/constans";
import { formatDateStringToKorea, formatDateStringToUTC, getOptionImage } from "helpers/format";
import CopyWrapper from "components/Common/CopyWrapper";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { getReservations } from "store/reservation/thunk";
import TermCondition from "components/Common/TermCondition";
import DropdownStatusNew from "components/Common/DropdownStatusNew";

registerLocale("en", en);
registerLocale("ko", ko);

const PreRegisterList = () => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    name: withDefault(StringParam, ""),
    emailContact: withDefault(StringParam, ""),
    phoneNumber: withDefault(StringParam, ""),
    region: withDefault(StringParam, ""),
    userSubsType: withDefault(StringParam, ""),
    experienceLevel: withDefault(StringParam, ""),
    companyName: withDefault(StringParam, ""),
    utm_campaign: withDefault(StringParam, ""),
    utm_source: withDefault(StringParam, ""),

    startDate: withDefault(StringParam, ""),
    endDate: withDefault(StringParam, ""),
  });

  const [fullNameSearch, setFullNameSearch] = useState<string>(
    query?.name || ""
  );
  const [emailSearch, setEmailSearch] = useState<string>(query?.email || "");
  const [phoneSearch, setPhoneSearch] = useState<string>(
    query?.phoneNumber || ""
  );
  const [regionSearch, setRegionSearch] = useState<string>(query?.region || "");

  const [reservationTypeSearch, setReservationTypeSearch] = useState<string>(
    query?.userSubsType || ""
  );

  const [experienceSearch, setExperienceSearch] = useState<string>(
    RESERVATION_EXPERIENCE_LEVEL?.find(
      (item: any) => item?.value == query?.experienceLevel
    )?.value || ""
  );

  const [companyNameSearch, setCompanyNameSearch] = useState<string>(
    query?.companyName || ""
  );

  const [campaignSearch, setCampaignSearch] = useState<string>(
    query?.utm_campaign || ""
  );

  const [sourceSearch, setSourceSearch] = useState<string>(
    PRE_REGISTER_SOURCE_OPTION?.find(
      (item: any) => item?.value == query?.utm_source
    )?.value || ""
  );


  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.startDate
      ? moment(query?.startDate || "", "Y-MM-DD").toDate()
      : null,
    query?.endDate ? moment(query?.endDate || "", "Y-MM-DD").toDate() : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const selectLayoutState = (state: any) => state.Reservation;

  const ReservationProperties = createSelector(selectLayoutState, (state) => ({
    reservations: state.reservations,
    isReservationLoading: state.isReservationLoading,
    isReservationSuccess: state.isReservationSuccess,

    error: state.error,
  }));

  // Inside your component
  const { reservations, isReservationLoading } = useSelector(
    ReservationProperties
  );

  // console.log(reservations);

  function loadTableData() {
    dispatch(getReservations(query));
  }

  useEffect(() => {
    loadTableData();
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      name: fullNameSearch.trim() || "",
      emailContact: emailSearch.trim() || "",
      phoneNumber: phoneSearch.trim() || "",
      region: regionSearch.trim() || "",
      userSubsType: reservationTypeSearch.trim() || "",
      experienceLevel: encodeURIComponent(experienceSearch.trim()) || "",
      companyName: companyNameSearch.trim() || "",
      utm_campaign: campaignSearch.trim() || "",
      utm_source: sourceSearch || "",
      startDate: startDate
        ? formatDateStringToUTC(
          moment(new Date(startDate))
            .startOf("day")
            .format("Y-MM-DD HH:mm:ss")
        )
        : "",
      endDate: endDate
        ? formatDateStringToUTC(
          moment(new Date(endDate)).endOf("day").format("Y-MM-DD HH:mm:ss")
        )
        : "",
      //sortBy: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        name: "",
        emailContact: "",
        phoneNumber: "",
        region: "",
        userSubsType: "",
        experienceLevel: "",
        companyName: "",
        utm_campaign: "",
        utm_source: "",
        startDate: "",
        endDate: "",
        sortBy: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setFullNameSearch("");
    setEmailSearch("");
    setPhoneSearch("");
    setRegionSearch("");
    setReservationTypeSearch("");
    setExperienceSearch("");
    setCompanyNameSearch("");
    setSourceSearch("");
    setCampaignSearch("");
    setDateSearch([null, null]);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div style={{ width: 35 }}>
              {reservations?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("User Type"),
        accessor: "userSubsType",
        thWidth: 150,
        filterable: false,
        sortable: false,
        thClass: "text-center",
        Cell: (cell: any) => {
          const row = cell?.row?.original;
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_RESERVATION_USER_TYPE[row?.userSubsType] || "secondary"
                  }`}
              >
                {t(
                  RESERVATION_USER_TYPE.filter(
                    (i: any) => i?.value == row?.userSubsType
                  )[0]?.label
                )}
              </span>
            </div>
          );
        },
      },

      {
        Header: t("Full Name"),
        accessor: "fullname",
        filterable: true,
        sortable: false,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            cell?.value ? (
              <div className="cursor-pointer" style={{ width: "135px" }}>
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            ) : "-"
          );
        },
      },

      {
        Header: t("Phone Number"),
        accessor: "phoneNumber",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          return cell?.value ? (
            <div className="cursor-pointer d-flex flex-column">
              <CopyWrapper contentCopy={cell?.value}>
                {`${cell?.value}`}
              </CopyWrapper>
            </div>
          ) : (
            "-"
          );
        },
      },

      {
        Header: t("Email"),
        accessor: "emailContact",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 220,
        Cell: (cell: any) => {
          return (
            cell?.value ? (
              <div className="cursor-pointer">
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            ) : "-"
          );
        },
      },

      {
        Header: t("Region"),
        accessor: "region",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            cell?.value && (
              <div className="cursor-pointer">
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },

      {
        Header: t("Experience"),
        accessor: "experience",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 170,
        Cell: (cell: any) => {
          const experience = cell?.value;

          return experience ? <>{experience}</> : "-";
        },
      },

      {
        Header: t("Bussiness Name"),
        accessor: "businessName",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const businessName = cell?.value;
          return businessName ? <>{businessName}</> : "-";
        },
      },

      {
        Header: t("Source"),
        accessor: "utm_source",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const source = cell?.value;
          return source ? (
            <div className="d-flex align-items-center">
              <div className="me-2">
                {(!['unknown'].includes(source)) ? <img src={getOptionImage(source)} width={20} height={20} alt={source} /> :
                  <span className={`bg-secondary`} style={{ width: "15px", height: '15px', borderRadius: '3px', display: "block" }}></span>
                }
              </div>
              <div>
                {t(PRE_REGISTER_SOURCE_OPTION?.find((item) => item?.value == source)?.label || "")}
              </div>
            </div>
          ) :
            <span
              className={`rounded-pill badge bg-secondary`}
              style={{
                fontSize: "0.8rem",
                lineHeight: 1,
                fontWeight: 400
              }}
            >
              {t(
                PRE_REGISTER_SOURCE_OPTION.filter(
                  (i: any) => i?.value == "unknown"
                )[0]?.label
              )}
            </span>;
        },
      },

      {
        Header: t("Campaign"),
        accessor: "utm_campaign",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            cell?.value ? (
              <div className="cursor-pointer">
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            ) : "-"
          );
        },
      },

      {
        Header: t("User Agreement"),
        accessor: "user_agreement",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 360,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <TermCondition
              conditions={{
                personal_info: item?.isAllowPersonalInfo == 1,
                event: item?.isAllowMarketInfo == 1,
              }}
            />
          );
        },
      },

      {
        Header: t("Created at"),
        accessor: "createdAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, reservations?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  useEffect(() => {
    document.title = `${t("Pre-Register")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Pre-Register")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <div className="d-block d-xl-flex justify-content-between align-items-start">
                    <div
                      className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                      style={{ maxWidth: "100%" }}
                    >
                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={reservations?.summary?.total || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Job Seeker")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={
                                      reservations?.summary?.totalApplicant || 0
                                    }
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Recruiter")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={
                                      reservations?.summary?.totalManager || 0
                                    }
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 ">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          isShow={!!fullNameSearch}
                          label={`${t("Full Name")}`}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Full Name")}...`}
                            value={fullNameSearch}
                            onChange={(e) => setFullNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Email")} isShow={!!emailSearch}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email")}...`}
                            value={emailSearch}
                            onChange={(e) => setEmailSearch(e.target.value)}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Phone Number")}`}
                          isShow={!!phoneSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Phone Number")}...`}
                            value={phoneSearch}
                            onChange={(e) => setPhoneSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Region")}`}
                          isShow={!!regionSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Region")}...`}
                            value={regionSearch}
                            onChange={(e) => setRegionSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Business Name")}`}
                          isShow={!!companyNameSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Business Name")}...`}
                            value={companyNameSearch}
                            onChange={(e) =>
                              setCompanyNameSearch(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("User Type")} isShow={true}>
                          <DropdownStatusNew
                            name="reservation_type"
                            dataList={RESERVATION_USER_TYPE || []}
                            placeholder={`${t("User Type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={reservationTypeSearch}
                            onChangeSelect={(event: any) =>
                              setReservationTypeSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_RESERVATION_USER_TYPE}

                          // hideSelectedOptions={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={t("Experience Level")}
                          isShow={true}
                        >
                          <DropdownOptionNew
                            name="experience_level"
                            dataList={RESERVATION_EXPERIENCE_LEVEL || []}
                            placeholder={`${t("Experience Level")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={experienceSearch}
                            onChangeSelect={(event: any) =>
                              setExperienceSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            hideSelectedOptions={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={t("Source")}
                          isShow={true}
                        >
                          <DropdownOptionNew
                            name={`source`}
                            dataList={PRE_REGISTER_SOURCE_OPTION || []}
                            placeholder={`${t("Select source")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={sourceSearch}
                            onChangeSelect={(event: any) => {
                              setSourceSearch(event?.value)
                            }}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            lstImage={true}
                            hideSelectedOptions={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Campaign")}`}
                          isShow={!!campaignSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Campaign")}...`}
                            value={campaignSearch}
                            onChange={(e) =>
                              setCampaignSearch(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-4 mt-md-3 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={12}
                        lg={12}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed sticky-table-pre-register"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={
                        reservations?.items?.length ? reservations?.items : []
                      }
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={reservations?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(reservations?.pagination?.total) /
                        Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isReservationLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PreRegisterList;
